import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import type { ResolvedClientReportBranding } from '../features/assessments-v2/types';
import { buildVerificationUrl, createVerificationQrDataUrl } from '../utils/documentVerification';
import type { SignedDocumentMetadata } from '../utils/documentVerification';

export interface AttendanceCertificateFormData {
  tenantSlug?: string | null;
  organizationName: string;
  organizationAddressLine1?: string;
  organizationAddressLine2?: string;
  organizationContactLine?: string;
  footerLegalLine?: string;
  branding?: ResolvedClientReportBranding | null;
  patientName: string;
  patientCpf: string;
  patientRg: string;
  patientAddress: string;
  patientMedicalRecordNumber?: string | null;
  appointmentDate: string;
  appointmentStartTime: string;
  appointmentEndTime: string;
  attendanceType: string;
  attendanceLocation: string;
  issueDate: string;
  issueLocation: string;
  professionalName: string;
  professionalRegistration: string;
  signatureLabel: string;
  additionalNotes: string;
  signedByUserId?: string | null;
  signature?: SignedDocumentMetadata | null;
}

const escapeHtml = (value?: string | null): string =>
  String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const formatDateBr = (value?: string): string => {
  if (!value) return '';

  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [year, month, day] = value.split('-');
    return `${day}/${month}/${year}`;
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }

  return parsed.toLocaleDateString('pt-BR');
};

const sanitizeFileNamePart = (value: string): string =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .toLowerCase();

const buildFallbackBranding = (data: AttendanceCertificateFormData): ResolvedClientReportBranding => ({
  clientId: '',
  source: 'fallback',
  displayName: data.organizationName || 'Clínica',
  legalName: data.organizationName || 'Clínica',
  cnpj: '—',
  reportSubtitle: 'Documento clínico',
  logoPrintUrl: '',
  logoMarkUrl: '',
  logoWordmarkUrl: '',
  logoAlt: data.organizationName || 'Clínica',
  addressLine1: data.organizationAddressLine1 || '',
  addressLine2: data.organizationAddressLine2 || '',
  contactLine: data.organizationContactLine || '',
  footerLegalLine: data.footerLegalLine || data.organizationName || 'Documento clínico',
  rawSettings: null,
});

async function toDataUrl(url?: string | null): Promise<string | null> {
  if (!url) return null;

  try {
    const res = await fetch(url);
    const blob = await res.blob();
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.warn('Não foi possível carregar imagem para impressão do atestado:', error);
    return null;
  }
}

export class AttendanceCertificatePDFService {
  static generateFileName(data: AttendanceCertificateFormData): string {
    const patientSlug = sanitizeFileNamePart(data.patientName || 'paciente');
    const dateSlug = data.appointmentDate || new Date().toISOString().slice(0, 10);
    return `atestado-presenca-${patientSlug}-${dateSlug}.pdf`;
  }

  private static async buildDocumentHtml(data: AttendanceCertificateFormData): Promise<string> {
    const branding = data.branding || buildFallbackBranding(data);
    const logoDataUrl = await toDataUrl(branding.logoPrintUrl || branding.logoWordmarkUrl || branding.logoMarkUrl);
    const revahubMarkDataUrl = await toDataUrl('/logos/revahub-mark.png');

    const safeBrandingDisplayName = escapeHtml(branding.displayName || branding.legalName || 'Relatório Clínico');
    const safeBrandingSubtitle = escapeHtml(branding.reportSubtitle || 'Documento clínico');
    const safeBrandingFooterLegal = escapeHtml(branding.footerLegalLine || 'Documento clínico');
    const safeLogoAlt = escapeHtml(branding.logoAlt || branding.displayName || 'Logo da clínica');
    const safeBrandingInitial = escapeHtml((branding.displayName || 'R').slice(0, 1).toUpperCase());
    const safePrintedAt = escapeHtml(new Date().toLocaleString('pt-BR'));
    const tenantContactHtml = [
      branding.addressLine1,
      branding.addressLine2,
      branding.contactLine,
      branding.cnpj && branding.cnpj !== '—' ? `CNPJ: ${branding.cnpj}` : '',
    ]
      .filter(Boolean)
      .map((line) => `<div>${escapeHtml(line)}</div>`)
      .join('');

    const safePatientName = escapeHtml(data.patientName || 'Não informado');
    const safePatientCpf = escapeHtml(data.patientCpf || 'Não informado');
    const safePatientRg = escapeHtml(data.patientRg || 'Não informado');
    const safePatientAddress = escapeHtml(data.patientAddress || 'Não informado');
    const safeAppointmentDate = escapeHtml(formatDateBr(data.appointmentDate) || 'Não informado');
    const safeAppointmentStart = escapeHtml(data.appointmentStartTime || 'Não informado');
    const safeAppointmentEnd = escapeHtml(data.appointmentEndTime || 'Não informado');
    const safeIssueDate = escapeHtml(formatDateBr(data.issueDate) || 'Não informado');
    const safeIssueLocation = escapeHtml(data.issueLocation || 'Não informado');
    const signature = data.signature || null;
    const verificationUrl = signature ? buildVerificationUrl(signature.verificationToken, data.tenantSlug || null) : '';
    const verificationQrDataUrl = signature ? await createVerificationQrDataUrl(verificationUrl).catch(() => '') : '';
    const safeDoctorName = escapeHtml(signature?.signedByName || data.signatureLabel || data.professionalName || 'Profissional responsável');
    const safeProfessionalTitle = escapeHtml(signature?.signedByRole || 'Profissional responsável pelo atendimento');
    const safeCouncilNumber = escapeHtml(signature?.signedByRegistration || data.professionalRegistration || 'Registro profissional não informado');
    const safeSignedAtLabel = escapeHtml(signature ? new Date(signature.signedAt).toLocaleString('pt-BR') : '—');
    const safeVerificationToken = escapeHtml(signature?.verificationToken || 'pendente');
    const safeVerificationUrl = escapeHtml(verificationUrl.replace(/^https?:\/\//, ''));
    const safeAdditionalNotes = escapeHtml(data.additionalNotes || '');

    const declarationText = `Declaro, para os devidos fins, que ${safePatientName}, CPF ${safePatientCpf}, RG ${safePatientRg}, residente em ${safePatientAddress}, compareceu a atendimento nesta clínica no dia ${safeAppointmentDate}, com início às ${safeAppointmentStart} e término às ${safeAppointmentEnd}.`;

    return `
      <style>
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        body {
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
          font-size: 10.5pt;
          line-height: 1.5;
          color: #1f2937;
          background: white;
        }

        .document-container {
          width: 794px;
          min-height: 1123px;
          margin: 0 auto;
          padding: 28px 28px 24px;
          background: white;
        }

        .header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 18px;
          padding: 14px 16px;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          background: #f8fafc;
          margin-bottom: 18px;
        }

        .header-left {
          display: flex;
          align-items: center;
          gap: 14px;
          min-width: 0;
        }

        .logo-container {
          width: 68px;
          height: 68px;
          flex: 0 0 68px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 8px;
          background: white;
          border: 1px solid #e2e8f0;
          overflow: hidden;
        }

        .logo-container img {
          max-width: 58px;
          max-height: 58px;
          object-fit: contain;
        }

        .logo-fallback {
          width: 42px;
          height: 42px;
          border-radius: 999px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #2e5a88;
          color: white;
          font-size: 16pt;
          font-weight: 700;
        }

        .tenant-title {
          font-size: 14.5pt;
          line-height: 1.2;
          color: #1e293b;
          font-weight: 700;
          margin-bottom: 3px;
        }

        .tenant-subtitle {
          font-size: 9pt;
          color: #64748b;
          line-height: 1.25;
        }

        .header-right {
          text-align: right;
          max-width: 265px;
          flex: 0 0 265px;
          font-size: 7.8pt;
          color: #64748b;
          line-height: 1.35;
        }

        .print-date {
          color: #94a3b8;
          margin-top: 6px;
        }

        .title-ribbon {
          background: #2e5a88;
          color: white;
          padding: 10px 16px;
          border-radius: 8px;
          margin-bottom: 18px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
        }

        .title-ribbon h1 {
          font-size: 15pt;
          line-height: 1.1;
          font-weight: 700;
          text-transform: none;
        }

        .title-ribbon span {
          font-size: 8.5pt;
          font-weight: 600;
          background: rgba(255,255,255,0.2);
          padding: 4px 12px;
          border-radius: 20px;
        }

        .evolution-section {
          background: white;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          margin-bottom: 24px;
          overflow: hidden;
        }

        .evolution-header {
          background: #f8fafc;
          padding: 12px 16px;
          border-bottom: 1px solid #e2e8f0;
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .evolution-icon {
          width: 28px;
          height: 28px;
          background: #2e5a88;
          border-radius: 6px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 14px;
          font-weight: 600;
        }

        .evolution-title {
          font-size: 12pt;
          font-weight: 600;
          color: #2e5a88;
        }

        .evolution-body {
          padding: 18px 20px;
          min-height: 250px;
        }

        .evolution-text {
          text-align: justify;
          white-space: pre-wrap;
          line-height: 1.75;
          font-size: 10.5pt;
          color: #374151;
        }

        .notes-title {
          font-size: 10pt;
          font-weight: 600;
          color: #2e5a88;
          margin-top: 18px;
          margin-bottom: 8px;
        }

        .signature-section {
          margin-top: 28px;
          page-break-inside: avoid;
          display: grid;
          grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
          gap: 14px;
          align-items: stretch;
        }

        .signature-panel,
        .verification-panel {
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          background: #fff;
          padding: 14px 16px;
          min-height: 124px;
        }

        .signature-line {
          border-top: 2px solid #2e5a88;
          width: 82%;
          margin: 34px auto 10px;
        }

        .signature-info {
          text-align: center;
        }

        .signer-name {
          font-size: 11pt;
          font-weight: 600;
          color: #1f2937;
          margin-bottom: 4px;
          line-height: 1.25;
        }

        .signer-role {
          font-size: 9.5pt;
          color: #4b5563;
          margin-bottom: 2px;
          line-height: 1.25;
        }

        .signer-registration {
          font-size: 9pt;
          color: #2e5a88;
          font-weight: 500;
          line-height: 1.25;
        }

        .verification-title {
          font-size: 8.8pt;
          font-weight: 700;
          color: #2e5a88;
          margin-bottom: 8px;
        }

        .verification-copy {
          display: grid;
          grid-template-columns: 66px minmax(0, 1fr);
          gap: 10px;
          align-items: start;
        }

        .verification-qr {
          width: 66px;
          height: 66px;
          border: 1px solid #e2e8f0;
          border-radius: 6px;
          overflow: hidden;
          background: white;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .verification-qr img {
          width: 66px;
          height: 66px;
          object-fit: contain;
        }

        .verification-text {
          font-size: 7.6pt;
          color: #4b5563;
          line-height: 1.35;
          word-break: break-word;
        }

        .verification-text strong {
          color: #1f2937;
        }

        .verification-link {
          color: #2e5a88;
          word-break: break-all;
        }

        .verification-placeholder {
          width: 100%;
          height: 100%;
          border: 1px dashed #cbd5e1;
          border-radius: 6px;
          background: linear-gradient(135deg, #f8fafc 0%, #ffffff 100%);
        }

        .footer {
          margin-top: 30px;
          padding: 12px 14px;
          border-top: 1px solid #e2e8f0;
          background: #f8fafc;
          border-radius: 8px;
        }

        .footer-content {
          display: flex;
          justify-content: space-between;
          align-items: center;
          font-size: 7.5pt;
          color: #64748b;
          gap: 16px;
        }

        .footer-left {
          line-height: 1.4;
        }

        .footer-right {
          text-align: right;
          display: flex;
          align-items: center;
          justify-content: flex-end;
          gap: 8px;
          min-width: 170px;
        }

        .revahub-mark {
          width: 28px;
          height: 28px;
          flex: 0 0 28px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .revahub-mark img {
          max-width: 28px;
          max-height: 28px;
          object-fit: contain;
        }

        .powered-title {
          color: #2e5a88;
          font-size: 8pt;
          font-weight: 700;
          line-height: 1.2;
        }

        .powered-copy {
          color: #94a3b8;
          font-size: 6.5pt;
          line-height: 1.2;
        }
      </style>
      <div class="document-container">
        <div class="header">
          <div class="header-left">
            <div class="logo-container">
              ${logoDataUrl ? `<img src="${logoDataUrl}" alt="${safeLogoAlt}" />` : `<div class="logo-fallback">${safeBrandingInitial}</div>`}
            </div>
            <div>
              <div class="tenant-title">${safeBrandingDisplayName}</div>
              <div class="tenant-subtitle">${safeBrandingSubtitle}</div>
            </div>
          </div>
          <div class="header-right">
            ${tenantContactHtml || `<div>${safeBrandingFooterLegal}</div>`}
            <div class="print-date">Impresso em ${safePrintedAt}</div>
          </div>
        </div>

        <div class="title-ribbon">
          <h1>Atestado de Presença</h1>
          <span>Comprovante de atendimento</span>
        </div>

        <div class="evolution-section">
          <div class="evolution-header">
            <div class="evolution-icon">A</div>
            <div class="evolution-title">Declaração de Comparecimento</div>
          </div>
          <div class="evolution-body">
            <div class="evolution-text">${declarationText}</div>
            <div class="evolution-text" style="margin-top: 18px;">Emitido em ${safeIssueLocation}, ${safeIssueDate}.</div>
            <div class="evolution-text" style="margin-top: 18px;">
              Atendido em ${safeAppointmentDate}, das ${safeAppointmentStart} às ${safeAppointmentEnd}.
            </div>
            <div class="evolution-text" style="margin-top: 4px;">
              Local do atendimento: ${escapeHtml(data.attendanceLocation || 'Não informado')}.
            </div>
            ${safeAdditionalNotes ? `<div class="notes-title">Observações</div><div class="evolution-text">${safeAdditionalNotes}</div>` : ''}
          </div>
        </div>

        <div class="signature-section">
          <div class="signature-panel">
            <div class="signature-line"></div>
            <div class="signature-info">
              <div class="signer-name">${safeDoctorName}</div>
              <div class="signer-role">${safeProfessionalTitle}</div>
              <div class="signer-registration">${safeCouncilNumber}</div>
            </div>
          </div>

          <div class="verification-panel">
            <div class="verification-title">Autenticidade do documento</div>
            ${signature
              ? `
                <div class="verification-copy">
                  <div class="verification-qr">
                    ${verificationQrDataUrl ? `<img src="${verificationQrDataUrl}" alt="QR code de autenticidade" />` : `<div class="verification-placeholder"></div>`}
                  </div>
                  <div class="verification-text">
                    <div>Documento assinado eletronicamente por <strong>${safeDoctorName}</strong>.</div>
                    <div>${safeProfessionalTitle} • ${safeCouncilNumber}</div>
                    <div>Assinado em: <strong>${safeSignedAtLabel}</strong></div>
                    <div>Código: <strong>${safeVerificationToken}</strong></div>
                    <div>Validação: <span class="verification-link">${safeVerificationUrl}</span></div>
                  </div>
                </div>
              `
              : `
                <div class="verification-copy">
                  <div class="verification-qr">
                    <div class="verification-placeholder"></div>
                  </div>
                  <div class="verification-text">
                    Documento pendente de assinatura eletrônica.
                  </div>
                </div>
              `}
          </div>
        </div>

        <div class="footer">
          <div class="footer-content">
            <div class="footer-left">
              <strong>${safeBrandingDisplayName} - documento eletrônico de atestado</strong><br>
              ${safeBrandingFooterLegal}<br>
              Documento confidencial - uso restrito a profissionais autorizados e ao paciente
            </div>
            <div class="footer-right">
              <div>
                <div class="powered-title">Powered by Revahub</div>
                <div class="powered-copy">© 2026 Revahub. Todos os direitos reservados.</div>
              </div>
              ${revahubMarkDataUrl ? `<div class="revahub-mark"><img src="${revahubMarkDataUrl}" alt="Revahub" /></div>` : ''}
            </div>
          </div>
        </div>
      </div>
    `;
  }

  private static async renderToPdf(data: AttendanceCertificateFormData): Promise<jsPDF> {
    const html = await this.buildDocumentHtml(data);
    const wrapper = document.createElement('div');
    wrapper.style.position = 'fixed';
    wrapper.style.left = '-10000px';
    wrapper.style.top = '0';
    wrapper.style.width = '794px';
    wrapper.style.background = '#ffffff';
    wrapper.style.zIndex = '-1';
    wrapper.innerHTML = html;
    document.body.appendChild(wrapper);

    try {
      const canvas = await html2canvas(wrapper, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#ffffff',
      });

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({ unit: 'mm', format: 'a4' });
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 8;
      const imgWidth = pageWidth - margin * 2;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      let heightLeft = imgHeight;
      let position = margin;

      pdf.addImage(imgData, 'PNG', margin, position, imgWidth, imgHeight);
      heightLeft -= pageHeight - margin * 2;

      while (heightLeft > 0) {
        position = heightLeft - imgHeight + margin;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', margin, position, imgWidth, imgHeight);
        heightLeft -= pageHeight - margin * 2;
      }

      return pdf;
    } finally {
      document.body.removeChild(wrapper);
    }
  }

  static async downloadPDF(data: AttendanceCertificateFormData): Promise<void> {
    const doc = await this.renderToPdf(data);
    doc.save(this.generateFileName(data));
  }

  static async printPDF(data: AttendanceCertificateFormData): Promise<void> {
    const doc = await this.renderToPdf(data);
    doc.autoPrint();
    const pdfUrl = doc.output('bloburl');
    window.open(pdfUrl, '_blank', 'noopener,noreferrer');
  }
}
